// Ported from the product app's ai-elements (Vercel AI SDK style). Message /
// MessageContent / Response mirror the app; Response uses Streamdown just like
// the app, minus the optional code/math/mermaid plugins. Internal links render
// as entity chips.
import { Bookmark } from "lucide-react";
import { Streamdown } from "streamdown";
import type { ComponentProps, HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

type StreamdownComponents = ComponentProps<typeof Streamdown>["components"];

type Role = "user" | "assistant" | "system";

export type MessageProps = HTMLAttributes<HTMLDivElement> & { from: Role };

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full max-w-[95%] flex-col gap-2",
      from === "user" ? "is-user ml-auto justify-end" : "is-assistant",
      className,
    )}
    {...props}
  />
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({ children, className, ...props }: MessageContentProps) => (
  <div
    className={cn(
      "is-user:dark flex w-fit min-w-0 max-w-full flex-col gap-2 overflow-hidden text-sm",
      "group-[.is-user]:ml-auto group-[.is-user]:rounded-lg group-[.is-user]:bg-secondary group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-secondary-foreground",
      "group-[.is-assistant]:text-foreground",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

/** Inline pill for an internal record mention (a horse, a saved document…). */
export function EntityChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/60 px-1.5 align-baseline text-[0.92em] font-medium text-foreground no-underline">
      <Bookmark className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      {children}
    </span>
  );
}

/** Internal links render as entity chips; external (http) links stay anchors. */
function MarkdownAnchor({ href, children }: { href?: string; children?: ReactNode }) {
  const external = /^https?:\/\//i.test(href ?? "");
  if (!external) return <EntityChip>{children}</EntityChip>;
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className="font-medium text-carrot-deep underline">
      {children}
    </a>
  );
}

const responseComponents = { a: MarkdownAnchor } as StreamdownComponents;

export type ResponseProps = ComponentProps<typeof Streamdown>;

/** Markdown answer renderer — same Streamdown the app uses. */
export const Response = ({ className, ...props }: ResponseProps) => (
  <Streamdown
    className={cn("size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0", className)}
    components={responseComponents}
    {...props}
  />
);
